import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import CredentialsTab from './CredentialsTab';
import accountService from '../services/accountService';
import hotelService from '../services/hotelService';
import partnerService from '../services/partnerService';
import pricingService from '../services/pricingService';
import productsService from '../services/productsService';
import syncTemplatesService from '../services/syncTemplatesService';
import CountrySelect from './CountrySelect';
import PartnerSelect from './PartnerSelect';
import SyncTemplatesReadOnly from './SyncTemplatesReadOnly';
import AccountSyncConnectTab, { stripMaskedSecrets, SECRET_FIELDS } from './AccountSyncConnectTab';
import MarketplaceOverridesTab from './MarketplaceOverridesTab';
import BillingTab, { BillingCreateSection } from './BillingTab';
import './AccountForm.css';
import CurrencySelect from './CurrencySelect';


const formatChainPriceLabel = (price) => {
  const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: price.currency }).format(price.unit_amount / 100);
  if (!price.recurring) return `${amount} one-time`;
  const { interval, interval_count } = price.recurring;
  return interval_count === 1 ? `${amount} / ${interval}` : `${amount} / ${interval_count} ${interval}s`;
};


const AccountForm = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, partnerid } = useParams();
  const { isSuperAdmin } = useAuth();
  const { triggerAccountsRefresh } = useAccount();
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const [loading, setLoading] = useState(false);
  // Create only: who pays (the chain itself or its partner), and the chain's own billing details when it
  // pays for itself. The lambda refuses a chain whose payer has no billing details.
  const [billing, setBilling] = useState({ billTo: 'CHAIN', details: {} });
  const [billingProblem, setBillingProblem] = useState(null);
  // The Billing tab on the edit page: saved by Update Hotel Chain, like every other tab here (BillingTab `mode`).
  const billingRef = useRef(null);
  const [error, setError] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [hotelsCurrentPage, setHotelsCurrentPage] = useState(1);
  // ?tab= decides the opening tab so a page that returns here (the Marketplace Override form) can land
  // the user back where they were instead of on Hotels.
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(location.search).get('tab') || 'hotels');

  const hotelsPerPage = 5;

  // SyncConnect settings live in their own table behind their own endpoint, but on screen they are just
  // another tab of this chain — so they are edited and saved exactly like every other field here, by
  // "Update Hotel Chain". This form owns the state; AccountSyncConnectTab only renders it.
  // Loaded lazily when the tab is first opened, and PUT on submit only if something was actually changed,
  // so a chain whose SyncConnect tab was never visited is never written to.
  const [scSettings, setScSettings] = useState(null);
  const [scDirty, setScDirty] = useState(false);

  // First open of the tab fills the state. Afterwards the only reload is the "Show/Hide secrets" toggle,
  // which must merge just the secret columns — a wholesale replace there would throw away any other edits
  // the user had already made on the tab. Neither path marks the form dirty; only a real edit does.
  const handleScLoad = useCallback((settings) => {
    setScSettings(prev => {
      if (!prev) return settings;
      const merged = { ...prev };
      SECRET_FIELDS.forEach(field => {
        merged[field] = settings[field];
        merged[`${field}Set`] = settings[`${field}Set`];
      });
      return merged;
    });
  }, []);

  const handleScChange = useCallback((next) => {
    setScSettings(next);
    setScDirty(true);
  }, []);

  // Chain override pricing
  const [platformProducts, setPlatformProducts] = useState([]);
  const [partnerSellsProducts, setPartnerSellsProducts] = useState(new Set());
  const [productConfigs, setProductConfigs] = useState({});
  const [chainStripeProducts, setChainStripeProducts] = useState([]);
  const [chainProductsLoading, setChainProductsLoading] = useState(false);
  const [chainProductsMsg, setChainProductsMsg] = useState(null);
  const [chainProductsSaving, setChainProductsSaving] = useState(false);
  const [chainPriceModal, setChainPriceModal] = useState(null);   // includes productType
  const [creatingChainProductKey, setCreatingChainProductKey] = useState(null); // { productType, key }
  const [archivingChainPriceId, setArchivingChainPriceId] = useState(null);
  const [productSeats, setProductSeats] = useState({});      // { [productId]: freeSeats }
  const [usedSeats, setUsedSeats] = useState({});            // { [productId]: usedSeats }
  const [seatsSaving, setSeatsSaving] = useState(false);

  const [partnerName, setPartnerName] = useState('');
  const [partnerIdState, setPartnerIdState] = useState('');
  const [formData, setFormData] = useState({
    COMPANYNAME: '',
    USERNAME: '',
    CONTACTNAME: '',
    CONTACTPHONE: '',
    CONTACTMOBILE: '',
    CONTACTEMAIL: '',
    BILLINGCONTACT: '',
    BILLINGADDRESS1: '',
    BILLINGADDRESS2: '',
    BILLINGADDRESS3: '',
    BILLINGADDRESS4: '',
    BILLINGCOUNTRY: '',
    BILLINGEMAIL: '',
    BIlLINGPHONE: '',
    COUNTRY: '',
    CURRENCY: 'USD',
    ALLOWYN: 'Y',
    INACTIVEDATE: '',
    PARENTACCOUNTID: '',
    DEMOYN: 'N',
    AWSREGION: 'eu-west-1',
    timezone: '',
    accounttype: '',
    confirmednda: 'N',
    confirmedgdpr: 'N',
    billingmethod: '',
    discount: '',
    vatno: '',
    stripeid: '',
    allowsubaccounts: 'N',
    cc_token_swap: 'N',
    cc_swap_guarantee_code: '',
    cc_swap_cash_code: '',
    enforce_scheduling: 'N',
    scheduling_lead_time: 0,
    cc_swap_template_ids: [],
    enterpriseid: '',
    partnerid: ''
  });
  const [syncTemplateOptions, setSyncTemplateOptions] = useState([]);

  useEffect(() => {
    if (id && (isViewMode || isEditMode)) {
      fetchAccount();
      fetchHotels();
    }
  }, [id, mode]);

  useEffect(() => {
    // Options for the "CC Swap Template Type" multi-select come from the sync-templates list.
    if (!isSuperAdmin) return;
    syncTemplatesService.listTemplates()
      .then(data => setSyncTemplateOptions(Array.isArray(data) ? data : (data?.templates || [])))
      .catch(() => setSyncTemplateOptions([]));
  }, [isSuperAdmin]);

  useEffect(() => {
    // Fetch partner name if we have a partnerid from URL or formData
    const pidToFetch = partnerid || formData.partnerid;
    if (pidToFetch && (isViewMode || isEditMode)) {
      fetchPartner(pidToFetch);
    }
  }, [partnerid, formData.partnerid, isViewMode, isEditMode]);

  useEffect(() => {
    // Reset to page 1 when hotels change
    setHotelsCurrentPage(1);
  }, [id]);

  useEffect(() => {
    if (partnerid && isCreateMode) {
      setFormData(prev => ({ ...prev, partnerid }));
    }
  }, [partnerid, isCreateMode]);

  useEffect(() => {
    if (isCreateMode && location.state?.prospect) {
      const p = location.state.prospect;
      setFormData(prev => ({
        ...prev,
        COMPANYNAME: p.company || '',
        CONTACTNAME: [p.first_name, p.last_name].filter(Boolean).join(' '),
        CONTACTEMAIL: p.email || '',
      }));
    }
  }, [isCreateMode, location.state]);

  useEffect(() => {
    if (id && activeTab === 'products') {
      loadChainProducts();
    }
  }, [id, activeTab, formData.partnerid]);

  const CHAIN_SLOTS = {
    upfront: { ext: `c-${id}-1yr-upfront`,   interval: null,    interval_count: null },
    annual:  { ext: `c-${id}-1yr-reoccuring`, interval: 'year',  interval_count: 1 },
    monthly: { ext: `c-${id}-1mo-reoccuring`, interval: 'month', interval_count: 1 },
  };

  const flashChainMsg = (text, type = 'success') => {
    setChainProductsMsg({ type, text });
    setTimeout(() => setChainProductsMsg(null), 4000);
  };

  const loadChainProducts = async () => {
    setChainProductsLoading(true);
    setChainProductsMsg(null);
    const pid = formData.partnerid || partnerid;
    try {
      const platformRes = await productsService.list();
      const products = platformRes.data?.products || [];
      setPlatformProducts(products);
      if (!products.length) return;

      const calls = [
        pricingService.listProducts(products[0].name),
        pricingService.getAccountProductSeats(id),
        ...products.map(p => pricingService.getConfig(p.name)),
      ];
      if (pid) calls.push(pricingService.getPartnerPlatformProducts(pid));

      const results = await Promise.all(calls);
      const stripeRes = results[0];
      const seatsRes = results[1];
      const configResults = pid ? results.slice(2, -1) : results.slice(2);
      const partnerSelectionRes = pid ? results[results.length - 1] : null;

      setChainStripeProducts(stripeRes.data?.products || []);

      const seatsMap = {};
      const usedMap = {};
      (seatsRes.data?.seats || []).forEach(s => {
        seatsMap[s.product_id] = s.free_seats;
        usedMap[s.product_id] = s.used_seats ?? 0;
      });
      setProductSeats(seatsMap);
      setUsedSeats(usedMap);

      const configs = {};
      products.forEach((p, i) => {
        configs[p.name] = configResults[i].data?.config || { price_prefix: '' };
      });
      setProductConfigs(configs);

      if (partnerSelectionRes) {
        const savedIds = new Set((partnerSelectionRes.data?.productIds || []).map(String));
        setPartnerSellsProducts(new Set(products.filter(p => savedIds.has(String(p.id))).map(p => p.name)));
      } else {
        setPartnerSellsProducts(new Set(products.map(p => p.name)));
      }
    } catch (err) {
      setChainProductsMsg({ type: 'error', text: 'Failed to load products' });
    } finally {
      setChainProductsLoading(false);
    }
  };

  const findChainProduct = (productType, key) => {
    const prefix = productConfigs[productType]?.price_prefix;
    if (!prefix) return null;
    const name = `${prefix}${CHAIN_SLOTS[key].ext}`;
    return chainStripeProducts.find(p => p.name === name) || null;
  };

  const createChainProduct = async (productType, key) => {
    const prefix = productConfigs[productType]?.price_prefix;
    const name = `${prefix}${CHAIN_SLOTS[key].ext}`;
    setCreatingChainProductKey({ productType, key });
    try {
      const res = await pricingService.createProduct(productType, { name });
      setChainStripeProducts(prev => [...prev, res.data.product]);
      flashChainMsg(`Product "${name}" created in Stripe`);
    } catch (err) {
      flashChainMsg(err.response?.data?.error?.message || 'Failed to create product', 'error');
    } finally {
      setCreatingChainProductKey(null);
    }
  };

  const openAddChainPrice = (productType, productId, key) => {
    const slot = CHAIN_SLOTS[key];
    setChainPriceModal({
      productType,
      productId,
      amount: '',
      currency: 'EUR',
      interval: slot.interval,
      interval_count: slot.interval_count,
      nickname: '',
      lookup_key: `${productConfigs[productType]?.price_prefix || ''}${slot.ext}`,
    });
  };

  const saveChainPrice = async () => {
    setChainProductsSaving(true);
    try {
      const res = await pricingService.createPrice(chainPriceModal.productType, {
        productId: chainPriceModal.productId,
        amount: chainPriceModal.amount,
        currency: chainPriceModal.currency,
        ...(chainPriceModal.interval ? { interval: chainPriceModal.interval, interval_count: chainPriceModal.interval_count } : {}),
        nickname: chainPriceModal.nickname || undefined,
        lookup_key: chainPriceModal.lookup_key || undefined,
      });
      const newPrice = res.data.price;
      setChainStripeProducts(prev => prev.map(p =>
        p.id === chainPriceModal.productId
          ? { ...p, prices: [...p.prices, newPrice].sort((a, b) => (a.unit_amount || 0) - (b.unit_amount || 0)) }
          : p
      ));
      setChainPriceModal(null);
      flashChainMsg('Price created');
    } catch (err) {
      flashChainMsg(err.response?.data?.error?.message || 'Failed to create price', 'error');
    } finally {
      setChainProductsSaving(false);
    }
  };

  const archiveChainPrice = async (productType, productId, priceId) => {
    if (!window.confirm('Archive this price? It will no longer be available.')) return;
    setArchivingChainPriceId(priceId);
    try {
      await pricingService.archivePrice(productType, priceId);
      setChainStripeProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, prices: p.prices.filter(pr => pr.id !== priceId) } : p
      ));
      flashChainMsg('Price archived');
    } catch (err) {
      flashChainMsg(err.response?.data?.error?.message || 'Failed to archive price', 'error');
    } finally {
      setArchivingChainPriceId(null);
    }
  };

  const renderChainProductSlot = (productType, key) => {
    const product = findChainProduct(productType, key);
    const prefix = productConfigs[productType]?.price_prefix;
    const fullName = prefix
      ? `${prefix}${CHAIN_SLOTS[key].ext}`
      : `{prefix}${CHAIN_SLOTS[key].ext}`;
    const isCreating = creatingChainProductKey?.productType === productType && creatingChainProductKey?.key === key;
    return (
      <div className="chain-product-slot">
        <div className="chain-product-name">{fullName}</div>
        {!product ? (
          <div className="chain-product-missing">
            <span className="chain-product-missing-text">Not yet created in Stripe.</span>
            <button
              type="button"
              className="btn-primary chain-btn-sm"
              onClick={() => createChainProduct(productType, key)}
              disabled={!prefix || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create in Stripe'}
            </button>
          </div>
        ) : (
          <div className="chain-prices-section">
            <div className="chain-prices-header">
              <span className="chain-prices-label">Prices</span>
              <button type="button" className="chain-btn-add-price" onClick={() => openAddChainPrice(productType, product.id, key)}>
                + Add Price
              </button>
            </div>
            {product.prices.length === 0 ? (
              <div className="chain-no-prices">No prices configured.</div>
            ) : (
              <table className="chain-prices-table">
                <thead>
                  <tr><th>Amount</th><th>Billing</th><th>Nickname</th><th>Lookup Key</th><th></th></tr>
                </thead>
                <tbody>
                  {product.prices.map(price => (
                    <tr key={price.id} className={archivingChainPriceId === price.id ? 'chain-price-row-archiving' : ''}>
                      <td className="cpt-amount">{formatChainPriceLabel(price)}</td>
                      <td className="cpt-interval">{price.recurring ? (price.recurring.interval_count === 1 ? `/ ${price.recurring.interval}` : `/ ${price.recurring.interval_count} ${price.recurring.interval}s`) : 'one-time'}</td>
                      <td>{price.nickname || <span className="cpt-muted">—</span>}</td>
                      <td className="cpt-lookup-key">{price.lookup_key || <span className="cpt-muted">—</span>}</td>
                      <td className="cpt-actions">
                        <button
                          type="button"
                          className="chain-btn-danger-sm"
                          onClick={() => archiveChainPrice(productType, product.id, price.id)}
                          disabled={archivingChainPriceId === price.id}
                        >
                          {archivingChainPriceId === price.id ? 'Archiving...' : 'Archive'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderChainProductOverrideSection = ({ id: productId, name, display_name }) => {
    const prefix = productConfigs[name]?.price_prefix;
    const seats = productSeats[productId] ?? 0;
    const used = usedSeats[productId] ?? 0;
    const total = seats + used;
    return (
      <div key={name} className="chain-override-section" style={{ marginBottom: '2rem' }}>
        <h4 className="chain-override-title">{display_name} Override Pricing</h4>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', padding: '0.75rem 1rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
            Free seats
          </label>
          <input
            type="number"
            min="0"
            value={seats}
            onChange={e => setProductSeats(prev => ({ ...prev, [productId]: parseInt(e.target.value) || 0 }))}
            style={{ width: '80px', padding: '0.3rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem' }}
          />
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>free {display_name} projects for this chain</span>
          {total > 0 && (
            <span style={{ fontSize: '0.8125rem', color: used > 0 ? '#dc2626' : '#6b7280', background: used > 0 ? '#fef2f2' : '#f3f4f6', border: `1px solid ${used > 0 ? '#fecaca' : '#e5e7eb'}`, borderRadius: '4px', padding: '0.15rem 0.5rem', whiteSpace: 'nowrap' }}>
              {used} / {total} used
            </span>
          )}
          <button
            type="button"
            className="btn-primary"
            style={{ fontSize: '0.8rem', padding: '0.35rem 0.875rem', marginLeft: 'auto' }}
            disabled={seatsSaving}
            onClick={async () => {
              setSeatsSaving(true);
              try {
                await pricingService.saveAccountProductSeats(id, [{ productId, freeSeats: productSeats[productId] ?? 0 }]);
                flashChainMsg(`Free seats saved for ${display_name}`);
              } catch (err) {
                flashChainMsg(`Failed to save free seats for ${display_name}`, 'error');
              } finally {
                setSeatsSaving(false);
              }
            }}
          >
            {seatsSaving ? 'Saving...' : 'Save Seats'}
          </button>
        </div>

        <p className="chain-override-desc">
          Chain-specific prices for {display_name}. These override partner and default pricing at checkout.
          Fallback order: chain → partner → default.
        </p>
        {!prefix && (
          <div className="chain-prefix-notice">
            Price prefix not configured for {display_name}. Set it in Admin → Products → {display_name} → Pricing Configuration.
          </div>
        )}
        <div className="chain-override-group">
          <div className="chain-override-group-label">
            Fixed Price Upfront <span className="chain-billing-badge badge-invoice">Invoice / Bank Transfer</span>
          </div>
          <p className="chain-override-group-note">Price × retention years = invoice total.</p>
          {renderChainProductSlot(name, 'upfront')}
        </div>
        <div className="chain-override-group">
          <div className="chain-override-group-label">
            Recurring Billing <span className="chain-billing-badge badge-card">Credit Card</span>
          </div>
          <p className="chain-override-group-note">Creates a Stripe subscription at checkout.</p>
          <div className="chain-recurring-label">Annual</div>
          {renderChainProductSlot(name, 'annual')}
          <div className="chain-recurring-label chain-recurring-label-spaced">Monthly</div>
          {renderChainProductSlot(name, 'monthly')}
        </div>
      </div>
    );
  };

  const fetchAccount = async () => {
    try {
      setLoading(true);
      const response = await accountService.getAccountById(id);
      // Handle both response formats: { account: {...} } or direct data
      const accountData = response.account || response;
      console.log('Fetched account data:', accountData);

      // Map API field names to form field names
      const mappedData = {
        COMPANYNAME: accountData.name || accountData.COMPANYNAME || '',
        USERNAME: accountData.username || accountData.USERNAME || '',
        CONTACTNAME: accountData.contactName || accountData.CONTACTNAME || '',
        CONTACTPHONE: accountData.contactPhone || accountData.CONTACTPHONE || '',
        CONTACTMOBILE: accountData.contactMobile || accountData.CONTACTMOBILE || '',
        CONTACTEMAIL: accountData.contactEmail || accountData.CONTACTEMAIL || '',
        BILLINGCONTACT: accountData.billingContact || accountData.BILLINGCONTACT || '',
        BILLINGADDRESS1: accountData.billingAddress1 || accountData.BILLINGADDRESS1 || '',
        BILLINGADDRESS2: accountData.billingAddress2 || accountData.BILLINGADDRESS2 || '',
        BILLINGADDRESS3: accountData.billingAddress3 || accountData.BILLINGADDRESS3 || '',
        BILLINGADDRESS4: accountData.billingAddress4 || accountData.BILLINGADDRESS4 || '',
        BILLINGCOUNTRY: accountData.billingCountry || accountData.BILLINGCOUNTRY || '',
        BILLINGEMAIL: accountData.billingEmail || accountData.BILLINGEMAIL || '',
        BIlLINGPHONE: accountData.billingPhone || accountData.BIlLINGPHONE || '',
        COUNTRY: accountData.country || accountData.COUNTRY || '',
        CURRENCY: accountData.currency || accountData.CURRENCY || 'USD',
        ALLOWYN: accountData.allowYn || accountData.ALLOWYN || 'Y',
        INACTIVEDATE: accountData.inactiveDate || accountData.INACTIVEDATE || '',
        PARENTACCOUNTID: accountData.parentAccountId || accountData.PARENTACCOUNTID || '',
        DEMOYN: accountData.demoYn || accountData.DEMOYN || 'N',
        AWSREGION: accountData.awsRegion || accountData.AWSREGION || 'eu-west-1',
        timezone: accountData.timezone || '',
        accounttype: accountData.accountType || accountData.accounttype || '',
        confirmednda: accountData.confirmedNda || accountData.confirmednda || 'N',
        confirmedgdpr: accountData.confirmedGdpr || accountData.confirmedgdpr || 'N',
        billingmethod: accountData.billingMethod || accountData.billingmethod || '',
        discount: accountData.discount || '',
        vatno: accountData.vatNo || accountData.vatno || '',
        stripeid: accountData.stripeId || accountData.stripeid || '',
        allowsubaccounts: accountData.allowSubaccounts || accountData.allowsubaccounts || 'N',
        cc_token_swap: accountData.cc_token_swap || accountData.ccTokenSwap || 'N',
        cc_swap_guarantee_code: accountData.cc_swap_guarantee_code || accountData.ccSwapGuaranteeCode || '',
        cc_swap_cash_code: accountData.cc_swap_cash_code || accountData.ccSwapCashCode || '',
        enforce_scheduling: accountData.enforceScheduling || accountData.enforce_scheduling || 'N',
        scheduling_lead_time: Number(accountData.schedulingLeadTime ?? accountData.scheduling_lead_time ?? 0),
        cc_swap_template_ids: (accountData.ccSwapTemplateIds || accountData.cc_swap_template_ids || []).map(Number),
        enterpriseid: accountData.enterpriseId || accountData.enterpriseid || '',
        partnerid: accountData.partnerId || accountData.partnerid || ''
      };

      console.log('Mapped form data:', mappedData);
      setFormData(mappedData);
    } catch (err) {
      setError('Failed to fetch account: ' + (err.response?.data?.message || err.message));
      console.error('Error fetching account:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHotels = async () => {
    try {
      const response = await hotelService.getAllHotels({ accountid: id });
      setHotels(response.hotels || []);
    } catch (err) {
      console.error('Failed to fetch hotels:', err);
    }
  };

  const fetchPartner = async (partnerId) => {
    try {
      const response = await partnerService.getPartnerById(partnerId);
      const partnerData = response.data?.partner || response.partner || response;
      setPartnerName(partnerData.companyname || partnerData.name || '');
      setPartnerIdState(partnerId);
    } catch (err) {
      console.error('Failed to fetch partner:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Toggle a sync-template id in the "CC Swap Template Type" multi-select.
  const toggleCcSwapTemplate = (templateId) => {
    const idNum = Number(templateId);
    setFormData(prev => {
      const current = prev.cc_swap_template_ids || [];
      const next = current.includes(idNum)
        ? current.filter(x => x !== idNum)
        : [...current, idNum];
      return { ...prev, cc_swap_template_ids: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.COMPANYNAME) {
      setError('Company Name is required');
      return;
    }

    if (!formData.COUNTRY) {
      setError('Country is required');
      return;
    }

    if (!formData.CURRENCY) {
      setError('Currency is required');
      return;
    }

    if (!formData.ALLOWYN) {
      setError('Status is required');
      return;
    }

    if (isCreateMode && billingProblem) {
      setError(billingProblem);
      return;
    }

    // BILLING SAVES WITH THE CHAIN — checked before anything is written, saved after the chain like the SyncConnect
    // settings. An untouched Billing tab has nothing pending and never blocks the save.
    const billingTab = isEditMode ? billingRef.current : null;
    const billingPending = billingTab && billingTab.pending();
    if (billingPending && billingPending.problem) {
      setError(billingPending.problem);
      window.scrollTo({ top: 0, behavior: 'smooth' });   // the message is at the top; Update is at the bottom
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isCreateMode) {
        await accountService.createAccount({ ...formData, billing });
        triggerAccountsRefresh();
        // Navigate back to returnTo location if provided, else to partner page
        if (location.state?.returnTo) {
          navigate(location.state.returnTo);
        } else if (partnerid || formData.partnerid) {
          const tab = location.state?.partnerTab || 'hotelchains';
          navigate(`/partners/${partnerid || formData.partnerid}?tab=${tab}`);
        } else {
          navigate('/partners');
        }
      } else if (isEditMode) {
        await accountService.updateAccount(id, formData);
        // Same submit, second table. Sequential rather than parallel so a SyncConnect failure surfaces
        // with the chain already saved, rather than leaving the user guessing which half landed.
        if (scDirty && scSettings) {
          await accountService.updateSyncConnectSettings(id, stripMaskedSecrets(scSettings));
        }
        if (billingPending) await billingTab.save();
        triggerAccountsRefresh();
        navigate(`/accounts/${id}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save account');
      console.error('Error saving account:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo);
    } else if (partnerid || formData.partnerid) {
      const tab = location.state?.partnerTab || 'hotelchains';
      navigate(`/partners/${partnerid || formData.partnerid}?tab=${tab}`);
    } else {
      navigate('/partners');
    }
  };

  if (loading && (isViewMode || isEditMode)) {
    return <div className="loading">Loading Hotel Chain...</div>;
  }

  return (
    <div className="account-form-container">
      {/* Breadcrumb */}
      {(partnerName || formData.COMPANYNAME) && (isViewMode || isEditMode) && (
        <div className="breadcrumb">
          {partnerName && (partnerIdState || partnerid || formData.partnerid) && (
            <>
              <Link to={`/partners/${partnerIdState || partnerid || formData.partnerid}`} className="breadcrumb-link">
                {partnerName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          <span className="breadcrumb-current">
            {formData.COMPANYNAME || 'Hotel Chain'}
          </span>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="account-form">
        <div className="form-section">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="COMPANYNAME">
                Hotel Chain Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="COMPANYNAME"
                name="COMPANYNAME"
                value={formData.COMPANYNAME}
                onChange={handleChange}
                disabled={isViewMode}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="ALLOWYN">Status <span className="required">*</span></label>
              <select
                id="ALLOWYN"
                name="ALLOWYN"
                value={formData.ALLOWYN}
                onChange={handleChange}
                disabled={isViewMode}
                required
              >
                <option value="">Select status...</option>
                <option value="Y">Active</option>
                <option value="N">Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="COUNTRY">Country <span className="required">*</span></label>
              <CountrySelect
                name="COUNTRY"
                value={formData.COUNTRY}
                onChange={handleChange}
                disabled={isViewMode}
              />
            </div>

            <div className="form-group">
              <label htmlFor="CURRENCY">Currency <span className="required">*</span></label>
              <CurrencySelect
                id="CURRENCY"
                name="CURRENCY"
                value={formData.CURRENCY}
                onChange={handleChange}
                disabled={isViewMode}
              />
            </div>
          </div>

          {isSuperAdmin && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="accounttype">Account Type</label>
                <input
                  type="text"
                  id="accounttype"
                  name="accounttype"
                  value={formData.accounttype}
                  onChange={handleChange}
                  disabled={isViewMode}
                />
              </div>
            </div>
          )}
        </div>

        {(isViewMode || isEditMode) && id && (
          <div className="account-tabs-section">
            <div className="account-tabs">
              <button
                type="button"
                className={`tab-button ${activeTab === 'hotels' ? 'active' : ''}`}
                onClick={() => setActiveTab('hotels')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M3 18v-8h3v8H3zm5 0v-8h4v8H8zm6 0v-8h3v8h-3zM2 9V4h16v5H2z" fill="currentColor"/>
                </svg>
                <span className="tab-label">Hotels</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
                onClick={() => setActiveTab('contact')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 10a4 4 0 100-8 4 4 0 000 8zm0 2c-4.42 0-8 1.79-8 4v2h16v-2c0-2.21-3.58-4-8-4z" fill="currentColor"/>
                </svg>
                <span className="tab-label">Contact Information</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'billing' ? 'active' : ''}`}
                onClick={() => setActiveTab('billing')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M2 8h16M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="tab-label">Billing Information</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'regional' ? 'active' : ''}`}
                onClick={() => setActiveTab('regional')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M2 10h16M10 2a8 8 0 018 8M10 2a8 8 0 00-8 8" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <span className="tab-label">Regional Settings</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'templates' ? 'active' : ''}`}
                onClick={() => setActiveTab('templates')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M6 7h8M6 10h8M6 13h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="tab-label">Sync Templates</span>
              </button>

              {isSuperAdmin && (
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
                  onClick={() => setActiveTab('settings')}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M17 10a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                  <span className="tab-label">Account Settings</span>
                </button>
              )}

              {isSuperAdmin && (
                <button
                  type="button"
                  className={`tab-button sa-only ${activeTab === 'syncconnect' ? 'active' : ''}`}
                  onClick={() => setActiveTab('syncconnect')}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <rect x="2.5" y="4" width="15" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 16h6M10 13v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M6 8.5l2 1.5-2 1.5M10.5 11.5h3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="tab-label">SyncConnect</span>
                </button>
              )}

              {isSuperAdmin && (
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
                  onClick={() => setActiveTab('products')}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2L2 7l8 5 8-5-8-5zM2 13l8 5 8-5M2 10l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="tab-label">Products</span>
                </button>
              )}
              <button
                type="button"
                className={`tab-button ${activeTab === 'credentials' ? 'active' : ''}`}
                onClick={() => setActiveTab('credentials')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="3" y="7" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M7 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="10" cy="12" r="1.5" fill="currentColor"/>
                </svg>
                <span className="tab-label">Credentials</span>
              </button>
              {isSuperAdmin && (
                <button
                  type="button"
                  className={`tab-button sa-only ${activeTab === 'marketplaceoverrides' ? 'active' : ''}`}
                  onClick={() => setActiveTab('marketplaceoverrides')}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4 5h12M4 10h12M4 15h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <circle cx="7" cy="5" r="2" fill="currentColor"/>
                    <circle cx="13" cy="10" r="2" fill="currentColor"/>
                    <circle cx="9" cy="15" r="2" fill="currentColor"/>
                  </svg>
                  <span className="tab-label">Marketplace Overrides</span>
                </button>
              )}
            </div>

            <div className="tab-content-area">
              {activeTab === 'hotels' && (
                <div className="tab-panel">
                  <div className="hotels-header">
                    <Link to={`/accounts/${id}/hotel/new`} className="btn-add-hotel">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Add Hotel
                    </Link>
                  </div>

                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Hotel Name</th>
                          <th>Hotel Code</th>
                          <th>Country</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hotels.length === 0 ? (
                          <tr><td colSpan="6" className="no-data">
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                              <path d="M12 36v-16h6v16h-6zm10 0v-16h8v16h-8zm12 0v-16h6v16h-6zM8 18V8h32v10H8z" fill="#d1d5db"/>
                            </svg>
                            <p>No hotels in this Hotel Chain</p>
                            <p className="no-data-hint">Create your first hotel to get started</p>
                          </td></tr>
                        ) : (
                          hotels
                            .slice(
                              (hotelsCurrentPage - 1) * hotelsPerPage,
                              hotelsCurrentPage * hotelsPerPage
                            )
                            .map((hotel) => (
                              <tr
                                key={hotel.hotelid}
                                className="clickable-row"
                                onClick={() => navigate(`/hotel/${hotel.hotelid}`)}
                                title="Click to view"
                              >
                                <td><span className="id-badge">{hotel.hotelid}</span></td>
                                <td className="primary-text">{hotel.hotelname || '-'}</td>
                                <td>{hotel.hotelcode || '-'}</td>
                                <td>{hotel.COUNTRY || '-'}</td>
                                <td>
                                  <span className={`status-badge status-${hotel.ALLOWYN === 'Y' ? 'active' : 'inactive'}`}>
                                    {hotel.ALLOWYN === 'Y' ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="actions-cell">
                                  <button
                                    className="btn-action btn-edit-action"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/hotel/${hotel.hotelid}/edit`);
                                    }}
                                    title="Edit"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M9.5 4.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination controls */}
                  {hotels.length > hotelsPerPage && (
                    <div className="pagination">
                      <button
                        onClick={() => setHotelsCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={hotelsCurrentPage === 1}
                      >
                        Previous
                      </button>
                      <span>
                        Page {hotelsCurrentPage} of {Math.ceil(hotels.length / hotelsPerPage)}
                      </span>
                      <button
                        onClick={() =>
                          setHotelsCurrentPage((prev) =>
                            Math.min(Math.ceil(hotels.length / hotelsPerPage), prev + 1)
                          )
                        }
                        disabled={hotelsCurrentPage >= Math.ceil(hotels.length / hotelsPerPage)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'contact' && (
                <div className="tab-panel">
                  <div className="form-section">
                    <h3>Contact Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="CONTACTNAME">Contact Name</label>
              <input
                type="text"
                id="CONTACTNAME"
                name="CONTACTNAME"
                value={formData.CONTACTNAME}
                onChange={handleChange}
                disabled={isViewMode}
              />
            </div>

            <div className="form-group">
              <label htmlFor="CONTACTEMAIL">Contact Email</label>
              <input
                type="email"
                id="CONTACTEMAIL"
                name="CONTACTEMAIL"
                value={formData.CONTACTEMAIL}
                onChange={handleChange}
                disabled={isViewMode}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="CONTACTPHONE">Contact Phone</label>
              <input
                type="text"
                id="CONTACTPHONE"
                name="CONTACTPHONE"
                value={formData.CONTACTPHONE}
                onChange={handleChange}
                disabled={isViewMode}
              />
            </div>

            <div className="form-group">
              <label htmlFor="CONTACTMOBILE">Contact Mobile</label>
              <input
                type="text"
                id="CONTACTMOBILE"
                name="CONTACTMOBILE"
                value={formData.CONTACTMOBILE}
                onChange={handleChange}
                disabled={isViewMode}
              />
            </div>
          </div>
                  </div>
                </div>
              )}

              {/* The shared Billing tab — same component as the hotel, chain settings, partner settings and vendor. */}
              {/* Kept mounted while editing (hidden on the other tabs), so Update Hotel Chain can save it. */}
              {(activeTab === 'billing' || isEditMode) && (
                <div className="tab-panel" style={activeTab === 'billing' ? undefined : { display: 'none' }}>
                  <BillingTab ref={billingRef} entity="chain" entityId={id} mode={isEditMode ? 'edit' : 'view'} />
                </div>
              )}

              {activeTab === 'regional' && (
                <div className="tab-panel">
                  <div className="form-section">
                    <h3>Regional Settings</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="COUNTRY">Country <span className="required">*</span></label>
              <CountrySelect
                name="COUNTRY"
                value={formData.COUNTRY}
                onChange={handleChange}
                disabled={isViewMode}
              />
            </div>

            <div className="form-group">
              <label htmlFor="CURRENCY">Currency <span className="required">*</span></label>
              <CurrencySelect
                id="CURRENCY"
                name="CURRENCY"
                value={formData.CURRENCY}
                onChange={handleChange}
                disabled={isViewMode}
              />
            </div>
          </div>

                  </div>
                </div>
              )}

              {activeTab === 'templates' && (
                <div>
                  <SyncTemplatesReadOnly accountId={id} />
                </div>
              )}

              {isSuperAdmin && activeTab === 'products' && (
                <div className="tab-panel">
                  {chainProductsMsg && (
                    <div className={`chain-products-msg chain-products-msg-${chainProductsMsg.type}`}>{chainProductsMsg.text}</div>
                  )}

                  {chainProductsLoading ? (
                    <div className="loading">Loading products...</div>
                  ) : platformProducts.length === 0 ? (
                    <div className="no-data-hint" style={{ padding: '1rem 0' }}>No products configured. Add products in Admin → Products.</div>
                  ) : partnerSellsProducts.size === 0 ? (
                    <div className="no-data-hint" style={{ padding: '1rem 0' }}>
                      The partner for this chain has not been assigned any products to sell. Set this in the partner's Products tab.
                    </div>
                  ) : (
                    platformProducts
                      .filter(p => partnerSellsProducts.has(p.name))
                      .map(renderChainProductOverrideSection)
                  )}
                </div>
              )}

              {isSuperAdmin && activeTab === 'syncconnect' && (
                <AccountSyncConnectTab
                  accountId={id}
                  isViewMode={isViewMode}
                  value={scSettings}
                  onLoad={handleScLoad}
                  onChange={handleScChange}
                />
              )}

              {activeTab === 'credentials' && (
                <div className="tab-panel">
                  <CredentialsTab entityType="account" entityId={id} />
                </div>
              )}

              {isSuperAdmin && activeTab === 'marketplaceoverrides' && (
                <div className="tab-panel">
                  <MarketplaceOverridesTab entityType="account" entityId={id} basePath={`/accounts/${id}`} />
                </div>
              )}

              {isSuperAdmin && activeTab === 'settings' && (
                <div className="tab-panel">
                  <div className="form-section">
                    <h3>Account Settings</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="DEMOYN">Demo Account</label>
              <select
                id="DEMOYN"
                name="DEMOYN"
                value={formData.DEMOYN}
                onChange={handleChange}
                disabled={isViewMode}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="allowsubaccounts">Allow Sub-Accounts</label>
              <select
                id="allowsubaccounts"
                name="allowsubaccounts"
                value={formData.allowsubaccounts}
                onChange={handleChange}
                disabled={isViewMode}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>

          </div>

          <fieldset className="form-section" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem 1.25rem 1.25rem', margin: '0.5rem 0 1rem' }}>
            <legend style={{ padding: '0 0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>CC Token Swap</legend>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cc_token_swap">CC Token Swap</label>
                <select
                  id="cc_token_swap"
                  name="cc_token_swap"
                  value={formData.cc_token_swap}
                  onChange={handleChange}
                  disabled={isViewMode}
                >
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="cc_swap_guarantee_code">CC Swap Guarantee Code</label>
                <input
                  type="text"
                  id="cc_swap_guarantee_code"
                  name="cc_swap_guarantee_code"
                  value={formData.cc_swap_guarantee_code}
                  onChange={handleChange}
                  disabled={isViewMode}
                />
              </div>

            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cc_swap_cash_code">CC Swap Cash Code</label>
                <input
                  type="text"
                  id="cc_swap_cash_code"
                  name="cc_swap_cash_code"
                  value={formData.cc_swap_cash_code}
                  onChange={handleChange}
                  disabled={isViewMode}
                />
              </div>

              <div className="form-group" style={{ flex: 1 }}>
                <label>CC Swap Template Type</label>
                <div style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '-0.25rem 0 0.5rem' }}>
                  Sync project template types CC swapping will occur on.
                </div>
                {syncTemplateOptions.length === 0 ? (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>No sync templates available.</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.75rem' }}>
                    {syncTemplateOptions.map(t => (
                      <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 400, cursor: isViewMode ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={(formData.cc_swap_template_ids || []).includes(Number(t.id))}
                          onChange={() => toggleCcSwapTemplate(t.id)}
                          disabled={isViewMode}
                        />
                        {t.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </fieldset>

          {/* SYNC SCHEDULING (V596) — with Enforce Scheduling on, the chain's Settings get a Scheduling tab where its
              admins propose sync project cutover dates and the partner commits to them, no sooner than the lead time. */}
          <fieldset className="form-section" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem 1.25rem 1.25rem', margin: '0.5rem 0 1rem' }}>
            <legend style={{ padding: '0 0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>Sync Scheduling</legend>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="enforce_scheduling">Enforce Scheduling</label>
                <select
                  id="enforce_scheduling"
                  name="enforce_scheduling"
                  value={formData.enforce_scheduling}
                  onChange={handleChange}
                  disabled={isViewMode}
                >
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="scheduling_lead_time">Scheduling Lead Time (days)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  id="scheduling_lead_time"
                  name="scheduling_lead_time"
                  value={formData.scheduling_lead_time}
                  onChange={handleChange}
                  disabled={isViewMode}
                />
              </div>
            </div>
          </fieldset>

          {isSuperAdmin && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="enterpriseid">Enterprise ID</label>
                <input
                  type="text"
                  id="enterpriseid"
                  name="enterpriseid"
                  value={formData.enterpriseid}
                  onChange={handleChange}
                  disabled={isViewMode}
                />
              </div>
            </div>
          )}

          {isSuperAdmin && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="partnerid">Partner</label>
                  <PartnerSelect
                    name="partnerid"
                    value={formData.partnerid}
                    onChange={handleChange}
                    disabled={isViewMode}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="confirmednda">Confirmed NDA</label>
                  <select
                    id="confirmednda"
                    name="confirmednda"
                    value={formData.confirmednda}
                    onChange={handleChange}
                    disabled={isViewMode}
                  >
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmedgdpr">Confirmed GDPR</label>
                  <select
                    id="confirmedgdpr"
                    name="confirmedgdpr"
                    value={formData.confirmedgdpr}
                    onChange={handleChange}
                    disabled={isViewMode}
                  >
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {(isEditMode || isViewMode) && formData.INACTIVEDATE && (
            <div className="form-group">
              <label htmlFor="INACTIVEDATE">Inactive Date</label>
              <input
                type="text"
                id="INACTIVEDATE"
                name="INACTIVEDATE"
                value={formData.INACTIVEDATE}
                disabled
              />
            </div>
          )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Show sections inline for create mode */}
        {isCreateMode && (
          <>
            <div className="form-section">
              <h3>Contact Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="CONTACTNAME-create">Contact Name</label>
                  <input
                    type="text"
                    id="CONTACTNAME-create"
                    name="CONTACTNAME"
                    value={formData.CONTACTNAME}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="CONTACTEMAIL-create">Contact Email</label>
                  <input
                    type="email"
                    id="CONTACTEMAIL-create"
                    name="CONTACTEMAIL"
                    value={formData.CONTACTEMAIL}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="CONTACTPHONE-create">Contact Phone</label>
                  <input
                    type="text"
                    id="CONTACTPHONE-create"
                    name="CONTACTPHONE"
                    value={formData.CONTACTPHONE}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="CONTACTMOBILE-create">Contact Mobile</label>
                  <input
                    type="text"
                    id="CONTACTMOBILE-create"
                    name="CONTACTMOBILE"
                    value={formData.CONTACTMOBILE}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <BillingCreateSection
              level="chain"
              partnerId={formData.partnerid || partnerid}
              value={billing}
              onChange={setBilling}
              onProblem={setBillingProblem}
            />
          </>
        )}

        <div className="form-actions">
          {!isViewMode && (
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isCreateMode ? 'Create Hotel Chain' : 'Update Hotel Chain'}
            </button>
          )}
          {isViewMode && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(`/accounts/${id}/edit`)}
            >
              Edit Hotel Chain
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={handleCancel}>
            {isViewMode ? 'Back' : 'Cancel'}
          </button>
        </div>
      </form>

      {chainPriceModal && (
        <div className="chain-modal-overlay">
          <div className="chain-modal">
            <h3>Add Price</h3>
            <div className="chain-form-row">
              <div className="chain-form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={chainPriceModal.amount}
                  onChange={e => setChainPriceModal(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00"
                  autoFocus
                />
              </div>
              <div className="chain-form-group">
                <label>Currency *</label>
                <CurrencySelect billing value={chainPriceModal.currency} onChange={e => setChainPriceModal(p => ({ ...p, currency: e.target.value }))} />
              </div>
            </div>
            {chainPriceModal.lookup_key && (
              <div className="chain-form-group">
                <label>Lookup Key</label>
                <input type="text" value={chainPriceModal.lookup_key} readOnly className="chain-input-readonly" />
              </div>
            )}
            <div className="chain-form-group">
              <label>Nickname <span className="chain-optional">(optional)</span></label>
              <input
                type="text"
                value={chainPriceModal.nickname}
                onChange={e => setChainPriceModal(p => ({ ...p, nickname: e.target.value }))}
                placeholder="e.g. Chain annual plan"
              />
            </div>
            <div className="chain-modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setChainPriceModal(null)}>Cancel</button>
              <button
                type="button"
                className="btn-primary"
                onClick={saveChainPrice}
                disabled={chainProductsSaving || !chainPriceModal.amount || parseFloat(chainPriceModal.amount) <= 0}
              >
                {chainProductsSaving ? 'Creating...' : 'Add Price'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountForm;
